import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'boss_site_access';
const SESSION_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const readStoredGrant = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    // Backwards-compat: an old "true" string from when we used sessionStorage / non-expiring access.
    if (raw === 'true') return true;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.expiresAt === 'number' && Date.now() < parsed.expiresAt) {
      return true;
    }
    localStorage.removeItem(STORAGE_KEY);
    return false;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return false;
  }
};

const writeStoredGrant = () => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ granted: true, expiresAt: Date.now() + SESSION_DURATION_MS }));
  } catch {
    /* ignore — quota / private mode etc. */
  }
};

const SiteGate = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [granted, setGranted] = useState(false);
  const [checking, setChecking] = useState(true);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Auth routes that must bypass the gate
  const isAuthRoute = ['/login', '/auth/callback', '/auth/google', '/forgot-password', '/reset-password'].some(
    p => window.location.pathname.startsWith(p)
  );

  useEffect(() => {
    if (authLoading) return;
    // Auth routes always bypass
    if (isAuthRoute) {
      setGranted(true);
      setChecking(false);
      return;
    }
    // Admin bypass
    if (user && user.email === 'info@bosscushions.com.au') {
      setGranted(true);
      setChecking(false);
      return;
    }
    // Check stored 7-day grant
    if (readStoredGrant()) {
      setGranted(true);
    }
    setChecking(false);
  }, [user, authLoading, isAuthRoute]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      // Retry once on network failure (handles transient 520s from Cloudflare)
      let res;
      try {
        res = await fetch(`${API}/api/site-access`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password }),
        });
      } catch {
        // Wait 2s and retry once
        await new Promise(r => setTimeout(r, 2000));
        res = await fetch(`${API}/api/site-access`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password }),
        });
      }
      if (res.ok) {
        writeStoredGrant();
        setGranted(true);
      } else {
        setError('Incorrect password');
      }
    } catch {
      setError('Server is temporarily unavailable. Please try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  if (checking || authLoading) {
    return (
      <div className="min-h-screen bg-[#f5f0eb] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (granted) return children;

  return (
    <div className="min-h-screen bg-[#f5f0eb] flex items-center justify-center px-4" data-testid="site-gate">
      <div className="w-full max-w-sm">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="mb-6">
            <h1 className="font-serif text-2xl text-gray-900 mb-1">Boss Cushions</h1>
            <p className="text-sm text-gray-500">This site is currently invite-only</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                placeholder="Enter password"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-center text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                autoFocus
                data-testid="site-gate-password"
              />
            </div>
            {error && <p className="text-red-500 text-xs" data-testid="site-gate-error">{error}</p>}
            <button
              type="submit"
              disabled={submitting || !password}
              className="w-full py-3 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="site-gate-submit"
            >
              {submitting ? 'Checking...' : 'Enter Site'}
            </button>
          </form>
          <p className="mt-5 text-xs text-gray-400">
            Already have an account? <a href="/login" className="text-teal-600 hover:underline">Sign in</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SiteGate;
